import { GroupHitbox, RectangleHitbox, type Hitbox } from "./hitbox";

export function interiorRectangles(hitbox: Hitbox): RectangleHitbox[] {
    return hitbox instanceof RectangleHitbox ? [hitbox]
        : hitbox instanceof GroupHitbox ? hitbox.hitboxes.filter((box): box is RectangleHitbox => box instanceof RectangleHitbox) : [];
}

/** Clip each tile to its actual room. All buffers are built once, never during movement. */
export function tiledInteriorGeometry(regions: readonly RectangleHitbox[], scale: number, tileSize = 8, textureSize = 128): {
    positions: Float32Array, uvs: Float32Array, indices: Uint32Array
} {
    const positions: number[] = [], uvs: number[] = [], indices: number[] = [];
    const inset = 0.5 / textureSize;
    const uv = (value: number): number => inset + value * (1 - 2 * inset);
    for (const { min, max } of regions) {
        for (let y = Math.floor(min.y / tileSize) * tileSize; y < max.y; y += tileSize) {
            for (let x = Math.floor(min.x / tileSize) * tileSize; x < max.x; x += tileSize) {
                const left = Math.max(min.x, x), right = Math.min(max.x, x + tileSize);
                const top = Math.max(min.y, y), bottom = Math.min(max.y, y + tileSize);
                const offset = positions.length / 2;
                positions.push(left * scale, top * scale, right * scale, top * scale, right * scale, bottom * scale, left * scale, bottom * scale);
                const u0 = uv((left - x) / tileSize), u1 = uv((right - x) / tileSize);
                const v0 = uv((top - y) / tileSize), v1 = uv((bottom - y) / tileSize);
                uvs.push(u0, v0, u1, v0, u1, v1, u0, v1);
                indices.push(offset, offset + 1, offset + 2, offset, offset + 2, offset + 3);
            }
        }
    }
    return { positions: new Float32Array(positions), uvs: new Float32Array(uvs), indices: new Uint32Array(indices) };
}

/** Large upstairs collision-only blockers are not visible walls. Thin permanent walls and columns are. */
export function permanentInteriorWalls(hitbox?: Hitbox): RectangleHitbox[] {
    return hitbox ? interiorRectangles(hitbox).filter(box => Math.min(box.max.x - box.min.x, box.max.y - box.min.y) <= 5) : [];
}
